import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getSession } from '../../getSession';
import API_ENDPOINTS from '../../config/apiEndpoints';

const NEW_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
const MASKING_API_URL = `${NEW_BASE_URL}${API_ENDPOINTS.COMMON.GET_FIELD_MASKED}`;

const funMaskFieldOddEven = (str, typOddEven = 'even', mask = '*') => {
    if (!str) return str;
    // ensure string
    const stringValue = String(str);
    let vArray = stringValue.split('');
    for (let i = 0; i < vArray.length; i++) {
        if (typOddEven === 'even') {
            if (((i + 1) % 2) === 0) {
                vArray[i] = mask;
            }
        } else {
            if (((i + 1) % 2) !== 0) {
                vArray[i] = mask;
            }
        }
    }
    return vArray.join("");
};

const maskingCache = new Map();
const maskingPromises = new Map();

const useFieldMasking = (pageCode) => {
    const [maskedFields, setMaskedFields] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchMaskedFields = async () => {
            if (!pageCode) return;
            const session = getSession();
            if (!session) return;
            
            const cacheKey = `${session.userId || "0"}_${pageCode}`;
            if (maskingCache.has(cacheKey)) {
                setMaskedFields(maskingCache.get(cacheKey));
                return;
            }

            if (maskingPromises.has(cacheKey)) {
                setIsLoading(true);
                try {
                    const data = await maskingPromises.get(cacheKey);
                    if (isMounted) setMaskedFields(data);
                } catch (error) {
                    console.error(`Error waiting for field masking promise for ${pageCode}:`, error);
                } finally {
                    if (isMounted) setIsLoading(false);
                }
                return;
            }

            setIsLoading(true);
            const payload = {
                Token: "-2295521862261168",
                Details: {
                    ParentId: parseInt(session.parentId || session.ParentId || session.SipPid || session.sipPid || session.CompanyId || "0", 10),
                    UserId: parseInt(session.userId || session.UserId || "0", 10),
                    PageCode: "PAG10372#PAG10002"
                }
            };
            
            const promise = axios.post(MASKING_API_URL, payload, {
                headers: { 'Content-Type': 'application/json' }
            }).then(response => {
                let parsedData = response.data?.d;
                if (typeof parsedData === 'string') {
                    try {
                        parsedData = JSON.parse(parsedData);
                    } catch (e) {}
                }
                const finalData = Array.isArray(parsedData) ? parsedData : [];
                maskingCache.set(cacheKey, finalData);
                return finalData;
            }).catch(error => {
                console.error(`Error fetching field masking for ${pageCode}:`, error);
                return [];
            }).finally(() => {
                maskingPromises.delete(cacheKey);
            });

            maskingPromises.set(cacheKey, promise);

            const finalData = await promise;
            if (isMounted) {
                setMaskedFields(finalData);
                setIsLoading(false);
            }
        };

        let isMounted = true;
        fetchMaskedFields();
        return () => { isMounted = false; };
    }, [pageCode]);

    const maskValue = useCallback((fieldName, value) => {
        if (!value || typeof value !== 'string' || !maskedFields.length) return value;
        
        // Match against typical key names that Kit19 might return
        const isMasked = maskedFields.some(f => {
            const ruleFieldName = (f.FieldName || f.Field_Name || f.ExposedName || f.ColumnName || '').toLowerCase();
            return ruleFieldName === fieldName.toLowerCase() && (f.HiddenType || '').toLowerCase() === 'masked';
        });

        if (isMasked) {
            return funMaskFieldOddEven(value, 'even', '*');
        }
        
        return value;
    }, [maskedFields]);

    const isHiddenField = useCallback((fieldName) => {
        if (!fieldName || !maskedFields.length) return false;
        return maskedFields.some(f => {
            const ruleFieldName = (f.FieldName || f.Field_Name || f.ExposedName || f.ColumnName || '').toLowerCase();
            return ruleFieldName === fieldName.toLowerCase() && (f.HiddenType || '').toLowerCase() === 'hidden';
        });
    }, [maskedFields]);

    const maskObject = useCallback((obj) => {
        if (!obj || typeof obj !== 'object' || !maskedFields.length) return obj;
        
        const maskedKeys = new Set();
        maskedFields.filter(f => (f.HiddenType || '').toLowerCase() === 'masked').forEach(f => {
            const fName = (f.FieldName || f.Field_Name || f.ExposedName || f.ColumnName || '').toLowerCase();
            maskedKeys.add(fName);
            if (fName === 'personname') {
                maskedKeys.add('name');
                maskedKeys.add('relatedto');
                maskedKeys.add('leadname');
            }
            if (fName === 'owner') {
                maskedKeys.add('creatorname');
                maskedKeys.add('assignedto');
            }
            if (fName === 'subject') {
                maskedKeys.add('title');
                maskedKeys.add('tasktitle');
            }
            if (fName === 'email' || fName === 'emailid') {
                maskedKeys.add('email');
                maskedKeys.add('emailid');
                maskedKeys.add('emailid1');
                maskedKeys.add('emailid2');
                maskedKeys.add('emailid3');
                maskedKeys.add('emails');
                maskedKeys.add('csvemailid');
                maskedKeys.add('csvemail');
            }
            if (fName === 'mobile' || fName === 'mobileno') {
                maskedKeys.add('mobile');
                maskedKeys.add('mobileno');
                maskedKeys.add('mobileno1');
                maskedKeys.add('mobileno2');
                maskedKeys.add('mobileno3');
                maskedKeys.add('phone');
                maskedKeys.add('phones');
                maskedKeys.add('csvmobileno');
                maskedKeys.add('personalmobileno');
            }
            if (fName === 'activityname') {
                maskedKeys.add('activityname');
                maskedKeys.add('eventname');
            }
            if (fName === 'activitydescription') {
                maskedKeys.add('activitydescription');
                maskedKeys.add('eventdescription');
            }
        });
        
        const newObj = { ...obj };
        for (const key in newObj) {
            if (newObj[key] && typeof newObj[key] === 'string') {
                if (maskedKeys.has(key.toLowerCase())) {
                    newObj[key] = funMaskFieldOddEven(newObj[key], 'even', '*');
                }
            }
        }
        Object.defineProperty(newObj, '__original', { value: obj, enumerable: false, writable: true });
        return newObj;
    }, [maskedFields]);

    return { maskedFields, isLoading, maskValue, maskObject, isHiddenField };
};

export default useFieldMasking;
